<template>
  <DataTable
    ref="dataTableRef"
    :loading="policyholdersStore.loading"
    :data="policyholdersStore.policyholders"
    :page-size="pageSize"
    :columns="columns"
    :row-key="rowKey"
    :search="true"
    @update-page="onPaginationChange"
    @update-sorter="onSorterChange"
    @update-filter="onFilterChange"
  >
    <n-button type="primary" @click="openFormModal()"> Add </n-button>
  </DataTable>

  <ConfirmModal ref="confirmModalRef" :on-confirm="onRemoveConfirm">
    <template #header>
      <h5>Confirm Deletion</h5>
    </template>
    <div class="text-center">
      <p class="mb-2">You're about to remove this policyholder from your account.</p>
      <h1 class="mb-2">Are you sure?</h1>
    </div>
  </ConfirmModal>

  <PolicyholderFormModal
    ref="policyholderFormModalRef"
    :policyholder="policyholdersStore.selectedPolicyholder"
    @on-submit="onSubmit"
  >
    <template v-if="policyholdersStore.selectedPolicyholderId"> Update Policyholder </template>
    <template v-else> Add new Policyholder </template>
  </PolicyholderFormModal>
</template>

<script setup lang="ts">
import type { ComponentExposed } from 'vue-component-type-helpers';
import { Trash, Pencil } from '@vicons/tabler';
import { NIcon, type DataTableCreateRowKey } from 'naive-ui';
import type DataTable from './DataTable.vue';
import { usePolicyholdersStore } from '../../agent-snap/stores/policyholders';
import type { DataTableColumn } from '../common/interfaces/data-table/data-table-props.interface';
import type { AddPolicyholderDto, PolicyholderDto, UpdatePolicyholderDto } from '../common/dtos/policyholder-list.dto';
import fullName from '../utils/full-name';
import DotsMenu from './DotsMenu.vue';
import type {
  DataTableFilterEmitParams,
  DataTablePaginationEmitParams,
  DataTableSorterEmitParams,
} from '../common/interfaces/data-table/data-table-emits.interface';
import type ConfirmModal from './ConfirmModal.vue';
import PolicyholderFormModal from './PolicyholderFormModal.vue';
import type { PolicyholderFormModalOnSubmitPropsModel } from '../common/models/contact-catalogs/policyholder-form-modal-props.model';

const dataTableRef = ref<ComponentExposed<typeof DataTable> | undefined>();
const policyholderFormModalRef = ref<ComponentExposed<typeof PolicyholderFormModal> | null>(null);
const policyholdersStore = usePolicyholdersStore();
const pageSize = 5;
const columns: DataTableColumn<PolicyholderDto>[] = [
  {
    title: 'Email',
    key: 'email' as keyof PolicyholderDto,
    sorter: true,
  },
  {
    title: 'Full name',
    key: 'firstName' as keyof PolicyholderDto,
    sorter: true,
    render: (rowData: PolicyholderDto) => {
      return fullName(rowData);
    },
  },
  {
    title: 'Business name',
    key: 'businessName' as keyof PolicyholderDto,
    sorter: true,
  },
  {
    key: 'actions',
    render: (rowData: PolicyholderDto) => {
      return h(DotsMenu, {
        menuItems: [
          {
            label: 'Edit',
            disabled: !rowData.canEdit,
            icon: () => {
              return h(NIcon, null, {
                default: () => h(Pencil),
              });
            },
            props: {
              onClick: () => {
                if (rowData.canEdit) {
                  openFormModal(rowData.id);
                }
              },
            },
          },
          {
            label: 'Delete',
            disabled: !rowData.canDelete,
            icon: () => {
              return h(NIcon, null, {
                default: () => h(Trash),
              });
            },
            props: {
              onClick: () => {
                if (rowData.canDelete) {
                  openConfirmModal(rowData.id);
                }
              },
            },
          },
        ],
      });
    },
  },
];
const rowKey: DataTableCreateRowKey<PolicyholderDto> = (row: PolicyholderDto) => row.id;

async function initPolicyholdersTable(): Promise<void> {
  await policyholdersStore.fetchPolicyholdersList({
    take: pageSize,
  });
}

async function onPaginationChange(options: DataTablePaginationEmitParams): Promise<void> {
  const skip = (options.currentPage - 1) * pageSize;
  await policyholdersStore.fetchPolicyholdersList({
    skip,
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof PolicyholderDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

async function onSorterChange(options: DataTableSorterEmitParams): Promise<void> {
  await policyholdersStore.fetchPolicyholdersList({
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof PolicyholderDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

async function onFilterChange(options: DataTableFilterEmitParams): Promise<void> {
  await policyholdersStore.fetchPolicyholdersList({
    take: pageSize,
    sortBy: options.sorter?.columnKey as keyof PolicyholderDto,
    sortOrder: options.sorter?.order,
    search: options.filterValue,
  });
}

function selectPolicyholder(id: number): void {
  policyholdersStore.selectedPolicyholderId = id;
}

async function removePolicyholder(): Promise<void> {
  await policyholdersStore.removeSelectedPolicyholder();
}

async function onRemoveConfirm() {
  await removePolicyholder();
  await initPolicyholdersTable();
}

const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null);

const openConfirmModal = (id: number) => {
  selectPolicyholder(id);
  confirmModalRef.value?.openModal();
};

onMounted(async () => {
  await initPolicyholdersTable();
});

async function openFormModal(id?: number): Promise<void> {
  if (id) {
    selectPolicyholder(id);

    await policyholdersStore.fetchSelectedPolicyholder();
  } else {
    policyholdersStore.selectedPolicyholderId = null;
  }

  if (policyholderFormModalRef.value) {
    policyholderFormModalRef.value.openModal();
  }
}

async function saveNew(model: AddPolicyholderDto): Promise<void> {
  const { $toastError } = useNuxtApp();

  try {
    await policyholdersStore.saveNew(model);
  } catch {
    $toastError('Something went wrong...');
  }
}

async function saveExisting(model: UpdatePolicyholderDto): Promise<void> {
  const { $toastError } = useNuxtApp();

  try {
    await policyholdersStore.saveExisting(model);
  } catch {
    $toastError('Something went wrong...');
  }
}

async function onSubmit(model: PolicyholderFormModalOnSubmitPropsModel): Promise<void> {
  const toSave: AddPolicyholderDto = {
    email: model.email,
    businessName: model.businessName,
    firstName: model.firstName,
    lastName: model.lastName,
  };

  if (policyholdersStore.selectedPolicyholderId) {
    const toSaveExisting: UpdatePolicyholderDto = toSave;

    await saveExisting(toSaveExisting);
  } else {
    await saveNew(toSave);
  }

  dataTableRef.value?.resetTable();
}
</script>
